import type { SVGProps } from "react";

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

function base({ size = 16, strokeWidth = 2, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...rest,
  };
}

export function ChevronRight(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.2, ...p })}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function ChevronLeft(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.2, ...p })}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function ChevronDown(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.2, ...p })}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function Plus(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.1, ...p })}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

export function Check(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 3.4, ...p })}>
      <path d="M4.5 12.5l5 5 10-11" />
    </svg>
  );
}

export function CircleCheck(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.2l2.6 2.6L16.2 9" />
    </svg>
  );
}

/** Shield with check — used on public certificate verification. */
export function ShieldCheck(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M12 3.2l7 2.6v5.4c0 4.2-2.9 7.1-7 9.1-4.1-2-7-4.9-7-9.1V5.8l7-2.6z" />
      <path d="M9.1 12.1l2.1 2.1 3.9-4.2" />
    </svg>
  );
}

export function Close(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.2, ...p })}>
      <path d="M5 5l14 14" />
      <path d="M19 5L5 19" />
    </svg>
  );
}

export function CircleClose(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6" />
      <path d="M15 9l-6 6" />
    </svg>
  );
}

export function Pencil(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

export function Reset(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M3 12a9 9 0 1 0 2.6-6.4" />
      <path d="M3 4v4h4" />
    </svg>
  );
}

/** AI sparkles — large 4-point star + two smaller companions. */
export function Sparkle({ size = 15, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...rest}
    >
      <path d="M11 2.5l1.55 4.55a.9.9 0 0 0 .55.55L17.65 9.15l-4.55 1.55a.9.9 0 0 0-.55.55L11 15.8l-1.55-4.55a.9.9 0 0 0-.55-.55L4.35 9.15l4.55-1.55a.9.9 0 0 0 .55-.55z" />
      <path d="M18.2 13.2l.85 2.35a.55.55 0 0 0 .35.35l2.35.85-2.35.85a.55.55 0 0 0-.35.35l-.85 2.35-.85-2.35a.55.55 0 0 0-.35-.35l-2.35-.85 2.35-.85a.55.55 0 0 0 .35-.35z" />
      <path d="M18.5 3.2l.55 1.55a.4.4 0 0 0 .25.25l1.55.55-1.55.55a.4.4 0 0 0-.25.25l-.55 1.55-.55-1.55a.4.4 0 0 0-.25-.25l-1.55-.55 1.55-.55a.4.4 0 0 0 .25-.25z" />
    </svg>
  );
}

export function Download(p: IconProps) {
  return (
    <svg {...base(p)}>
      <path d="M12 4v11" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 19h16" />
    </svg>
  );
}

export function Play(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M8 6.5v11l9-5.5z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function Pause(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M8 6.5h2.8v11H8z" fill="currentColor" stroke="none" />
      <path d="M13.2 6.5H16v11h-2.8z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function Volume(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M4.5 9.5h3.2L12 6.2v11.6l-4.3-3.3H4.5z" />
      <path d="M15.2 9.2a3.2 3.2 0 0 1 0 5.6" />
      <path d="M17.4 7a5.6 5.6 0 0 1 0 10" />
    </svg>
  );
}

export function VolumeMute(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M4.5 9.5h3.2L12 6.2v11.6l-4.3-3.3H4.5z" />
      <path d="M16 9.5l4 5" />
      <path d="M20 9.5l-4 5" />
    </svg>
  );
}

export function ArrowRight(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 2.2, ...p })}>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

export function LogIn(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M9 4H5v16h4" />
      <path d="M14 8l4 4-4 4" />
      <path d="M18 12H9" />
    </svg>
  );
}

export function Mail(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M3.5 7.5l8.5 6 8.5-6" />
    </svg>
  );
}

export function Globe(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

export function Folder(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h3.6l1.6 2H18.5a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
    </svg>
  );
}

export function History(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3" />
      <path d="M4.5 5.5v3.8H8.2" />
      <path d="M12 8.5V12l2.4 1.6" />
    </svg>
  );
}

export function Star({ size = 14, className, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
      {...rest}
    >
      <path d="M12 3.2l2.4 5.7 6.2.6-4.7 4.1 1.4 6.1L12 16.7 6.7 19.7l1.4-6.1L3.4 9.5l6.2-.6z" />
    </svg>
  );
}

export function Grid(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function Form(p: IconProps) {
  return (
    <svg {...base({ strokeWidth: 1.8, ...p })}>
      <path d="M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path d="M8.5 9h7" />
      <path d="M8.5 13h7" />
      <path d="M8.5 17h4" />
    </svg>
  );
}
